import { useForm } from '@mantine/form';
import {
  Modal,
  TextInput,
  Stack,
  Button,
  NumberInput,
  Select,
  Textarea,
  Group,
  ActionIcon,
  SegmentedControl,
  Text,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useState, useEffect } from 'react';
import { notifications } from '@mantine/notifications';
import { env } from '../../utils/env';
import { SimpleCategoryHeader, SimpleCategory } from '../../types/budget';
import { TransactionFormData, TransactionType } from '../../types/transaction';

interface CategoryHeader {
  id: string;
  name: string;
  categories: Category[];
}

interface Category {
  id: string;
  name: string;
  header_id: string;
}

interface TransactionModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (values: TransactionFormData) => Promise<void>;
  transaction?: TransactionFormData;
  headers: CategoryHeader[];
  onCreateHeader: (name: string) => Promise<CategoryHeader>;
  onCreateCategory: (name: string, headerId: string) => Promise<Category>;
  accounts: { id: string; name: string; institution_name?: string }[];
}

export const TransactionModal = ({
  opened,
  onClose,
  onSubmit,
  transaction,
  headers,
  onCreateHeader,
  onCreateCategory,
  accounts,
}: TransactionModalProps) => {
  const [newHeader, setNewHeader] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [isCreatingHeader, setIsCreatingHeader] = useState(false);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<TransactionFormData>({
    initialValues: transaction || {
      amount: 0,
      date: new Date(),
      description: '',
      account_id: '',
      transaction_type: TransactionType.EXPENSE,
      location_manual_input: '',
    },
    validate: {
      amount: (value) => value === 0 ? 'Amount cannot be zero' : null,
      description: (value) => !value ? 'Description is required' : null,
      account_id: (value) => !value ? 'Account is required' : null,
      header_id: (value) => {
        // Require header for expense transactions
        if (form.values.transaction_type === TransactionType.EXPENSE && !value) {
          return 'Header is required for expenses';
        }
        return null;
      },
      category_id: (value) => {
        // Require category for expense transactions
        if (form.values.transaction_type === TransactionType.EXPENSE && form.values.header_id && !value) {
          return 'Category is required for expenses';
        }
        return null;
      },
      transfer_account: (value) => {
        // Require transfer account for transfer transactions
        if (form.values.transaction_type === TransactionType.TRANSFER && !value) {
          return 'Transfer account is required';
        }
        return null;
      },
    },
  });

  useEffect(() => {
    if (transaction) {
      console.log('TransactionModal - Setting transaction values:', transaction);
      console.log('Modal useEffect - Received location prop:', transaction.location);
      const manualInput = transaction.location?.manual_input;
      console.log('Modal useEffect - Extracted manual_input:', manualInput);
      form.setValues({
        ...transaction,
        location_manual_input: manualInput || '',
      });
    }
  }, [transaction]);

  // Reset category when transaction type changes
  useEffect(() => {
    if (form.values.transaction_type !== TransactionType.EXPENSE) {
      form.setFieldValue('header_id', undefined);
      form.setFieldValue('category_id', undefined);
    }
  }, [form.values.transaction_type]);

  const handleClose = () => {
    form.reset();
    setNewHeader('');
    setNewCategory('');
    setIsCreatingHeader(false);
    setIsCreatingCategory(false);
    onClose();
  };

  const selectedHeader = headers.find(h => h.id === form.values.header_id);
  const categories = selectedHeader?.categories || [];

  console.log('TransactionModal - Form Values:', form.values);
  console.log('TransactionModal - Headers:', headers);
  console.log('TransactionModal - Selected Header:', selectedHeader);
  console.log('TransactionModal - Categories:', categories);

  const handleCreateHeader = async () => {
    if (!newHeader.trim()) return;
    try {
      setIsLoading(true);
      const header = await onCreateHeader(newHeader);
      form.setFieldValue('header_id', header.id);
      setNewHeader('');
      setIsCreatingHeader(false);
      notifications.show({
        color: 'green',
        title: 'Success',
        message: 'Header created successfully',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error creating header';
      notifications.show({
        color: 'red',
        title: 'Error',
        message,
      });
      console.error('Error creating header:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.trim() || !form.values.header_id) return;
    try {
      setIsLoading(true);
      const category = await onCreateCategory(newCategory, form.values.header_id);
      form.setFieldValue('category_id', category.id);
      setNewCategory('');
      setIsCreatingCategory(false);
      notifications.show({
        color: 'green',
        title: 'Success',
        message: 'Category created successfully',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error creating category';
      notifications.show({
        color: 'red',
        title: 'Error',
        message,
      });
      console.error('Error creating category:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (values: TransactionFormData) => {
    try {
      setIsLoading(true);
      await onSubmit(values);
      handleClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error submitting transaction';
      notifications.show({
        color: 'red',
        title: 'Error',
        message,
      });
      console.error('Error submitting transaction:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Group accounts by institution name
  const groupedAccounts = accounts.reduce((acc, account) => {
    const institution = account.institution_name || 'Other Accounts';
    if (!acc[institution]) {
      acc[institution] = [];
    }
    acc[institution].push(account);
    return acc;
  }, {} as Record<string, typeof accounts>);

  // Convert to Select component data format with groups
  const accountOptions = Object.entries(groupedAccounts).map(([institution, accounts]) => ({
    group: institution,
    items: accounts.map(acc => ({
      value: acc.id.toString(),
      label: acc.name
    }))
  }));

  // Prevent selecting the same account for both source and destination in transfers
  const sourceAccountOptions = accountOptions;
  const destinationAccountOptions = form.values.account_id
    ? accountOptions.map(group => ({
      group: group.group,
      items: group.items.filter(item => item.value !== form.values.account_id)
    })).filter(group => group.items.length > 0)
    : accountOptions;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={transaction ? 'Edit Transaction' : 'New Transaction'}
      size="lg"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <SegmentedControl
            data={[
              { label: 'Expense', value: TransactionType.EXPENSE },
              { label: 'Income', value: TransactionType.INCOME },
              { label: 'Transfer', value: TransactionType.TRANSFER },
            ]}
            {...form.getInputProps('transaction_type')}
          />

          <Group grow>
            <NumberInput
              label="Amount"
              placeholder="0.00"
              decimalScale={2}
              min={0}
              required
              {...form.getInputProps('amount')}
            />
            <DatePickerInput
              label="Date"
              required
              {...form.getInputProps('date')}
            />
          </Group>

          {form.values.transaction_type === TransactionType.TRANSFER ? (
            <>
              <Select
                label="From Account"
                placeholder="Select source account"
                data={sourceAccountOptions}
                required
                {...form.getInputProps('account_id')}
                onChange={(value) => {
                  form.setFieldValue('account_id', value || '');
                  // Clear destination account if it's the same as source
                  if (value === form.values.transfer_account) {
                    form.setFieldValue('transfer_account', '');
                  }
                }}
              />
              <Select
                label="To Account"
                placeholder="Select destination account"
                data={destinationAccountOptions}
                required
                {...form.getInputProps('transfer_account')}
              />
            </>
          ) : (
            <Select
              label="Account"
              placeholder="Select account"
              data={accountOptions}
              required
              {...form.getInputProps('account_id')}
            />
          )}

          <TextInput
            label="Description"
            placeholder="Transaction description"
            required
            {...form.getInputProps('description')}
          />

          {form.values.transaction_type === TransactionType.EXPENSE && (
            <>
              {isCreatingHeader ? (
                <Group>
                  <TextInput
                    style={{ flex: 1 }}
                    label="New Header"
                    placeholder="Enter new header name"
                    value={newHeader}
                    onChange={(e) => setNewHeader(e.target.value)}
                    disabled={isLoading}
                  />
                  <Button onClick={handleCreateHeader} mt={24} loading={isLoading}>Create</Button>
                  <Button variant="subtle" onClick={() => setIsCreatingHeader(false)} mt={24} disabled={isLoading}>Cancel</Button>
                </Group>
              ) : (
                <Group>
                  <Select
                    style={{ flex: 1 }}
                    label="Header"
                    placeholder="Select header"
                    data={headers.map(h => ({ value: h.id.toString(), label: h.name }))}
                    value={form.values.header_id}
                    onChange={(value) => {
                      const oldHeaderId = form.values.header_id;
                      form.setFieldValue('header_id', value || undefined);
                      if (oldHeaderId !== value) {
                        form.setFieldValue('category_id', undefined);
                      }
                    }}
                    disabled={isLoading}
                    required
                  />
                  <ActionIcon onClick={() => setIsCreatingHeader(true)} mt={24} disabled={isLoading}>+</ActionIcon>
                </Group>
              )}

              {form.values.header_id && (
                isCreatingCategory ? (
                  <Group>
                    <TextInput
                      style={{ flex: 1 }}
                      label="New Category"
                      placeholder="Enter new category name"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      disabled={isLoading}
                    />
                    <Button onClick={handleCreateCategory} mt={24} loading={isLoading}>Create</Button>
                    <Button variant="subtle" onClick={() => setIsCreatingCategory(false)} mt={24} disabled={isLoading}>Cancel</Button>
                  </Group>
                ) : (
                  <Group>
                    <Select
                      style={{ flex: 1 }}
                      label="Category"
                      placeholder="Select category"
                      data={categories.map(c => ({ value: c.id.toString(), label: c.name }))}
                      value={form.values.category_id}
                      onChange={(value) => {
                        console.log('Category Select - Selected Value:', value);
                        form.setFieldValue('category_id', value || undefined);
                      }}
                      disabled={isLoading}
                      required
                    />
                    <ActionIcon onClick={() => setIsCreatingCategory(true)} mt={24} disabled={isLoading}>+</ActionIcon>
                  </Group>
                )
              )}
            </>
          )}

          <TextInput
            label="Merchant"
            placeholder="Enter merchant name (optional)"
            {...form.getInputProps('merchant_name')}
            disabled={isLoading}
          />

          <TextInput
            label="Location"
            placeholder="Enter location (optional)"
            {...form.getInputProps('location_manual_input')}
            disabled={isLoading}
          />

          <Textarea
            label="Notes"
            placeholder="Add notes (optional)"
            {...form.getInputProps('notes')}
            disabled={isLoading}
          />

          <Group justify="flex-end">
            <Button variant="subtle" onClick={handleClose} disabled={isLoading}>Cancel</Button>
            <Button type="submit" loading={isLoading}>
              {transaction ? 'Update' : 'Add'} Transaction
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default TransactionModal; 